<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useHead } from 'unhead';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import NumberAnimation from "vue-number-animation";
import RoleStaff from '../components/staff/RoleStaff.vue';

import StaffListEmbed from '../components/StaffListEmbed.vue';
import Footer from '../components/Footer.vue';

import UtilityService from '../services/UtilityService';

// Composables
const { t } = useI18n();

// Reactive data
const recentStaffLists = ref([]);
const airingStaffLists = ref([]);

const totalStaffLists = ref(0);
const totalUpdated = ref(0);
const totalContributors = ref(0);

const carouselIndex = ref(0);
let carouselInterval = null;

const exampleCarousel = [
    {
        name: "NARUTO",
        original: "NARUTO -ナルト-",
        slug: "naruto",
        episode: "133",
        roleData: {"name":"Key Animation","original":"原画","staff":[{"ja":"松本憲生","en":"Norio Matsumoto","id":100991},{"ja":"井上敦子","en":"Atsuko Inoue","id":104477},{"ja":"松竹徳幸","en":"Tokuyuki Matsutake","id":102673},{"ja":"鈴木博文","en":"Hirofumi Suzuki","id":101188},{"ja":"若林厚史","en":"Atsushi Wakabayashi","id":105805}]}
    },
    {
        name: "DEVILMAN crybaby",
        original: "デビルマン クライベイビー",
        slug: "devilman-crybaby",
        episode: "05",
        roleData: {"name":"Key Animation","original":"原画","staff":[{"ja":"押山清高","en":"Kiyotaka Oshiyama","id":119806},{"ja":"","en":""},{"ja":"西垣庄子","en":"Shouko Nishigaki","id":131008},{"ja":"亀澤蘭","en":"Ran Kamezawa","id":132969},{"ja":"鈴木かんち","en":"Kanchi Suzuki","id":131013}]}
    },
    {
        name: "Maison Ikkoku",
        original: "めぞん一刻",
        slug: "maison-ikkoku",
        episode: "39",
        roleData: {"name":"Key Animation","original":"原画","staff":[{"ja":"大塚伸治","en":"Shinji Ootsuka","id":124192},{"ja":"二木真希子","en":"Makiko Futaki","id":133911},{"ja":"遠藤正明","en":"Masaaki Endou","id":131309},{"ja":"河口俊夫","en":"Toshio Kawaguchi","id":111747},{"ja":"近藤勝也","en":"Katsuya Kondou","id":101825}]}
    },
    {
        name: "Ninja Slayer from Animation",
        original: "ニンジャスレイヤー フロムアニメイシヨン",
        slug: "ninja-slayer-from-animation",
        episode: "01",
        roleData: {"name":"Key Animation","original":"原画","staff":[{"ja":"佐藤利幸","en":"Toshiyuki Satou","id":107357},{"ja":"斉藤健吾","en":"Kengo Saitou","id":136369},{"ja":"平田雄三","en":"Katsuzou Hirata","id":112997},{"ja":"芳垣祐介","en":"Yuusuke Yoshigaki","id":109521},{"ja":"杉本ミッシェル","en":"Michel Sugimoto","id":136366}]}
    },
    {
        name: "Ranma 1/2: Nettou-hen",
        original: "らんま1/2 熱闘編",
        slug: "ranma-1-2-nettou-hen",
        episode: "113",
        roleData: {"name":"Key Animation","original":"原画","staff":[{"ja":"鈴木博文","en":"Hirofumi Suzuki","id":101188},{"ja":"松竹徳幸","en":"Tokuyuki Matsutake","id":102673},{"ja":"桂憲一郎","en":"Kenichirou Katsura","id":110843},{"ja":"新号靖","en":"Yasushi Shingou","id":105767},{"ja":"石田敦子","en":"Atsuko Ishida","id":101575}]}
    },
    {
        name: "NG Kishi Ramune & 40",
        original: "NG騎士ラムネ＆４０",
        slug: "ng-kishi-ramune-40",
        episode: "20",
        roleData: {"name":"Key Animation","original":"原画","staff":[{"ja":"大河原晴男","en":"Haruo Oogawara","id":103192},{"ja":"岸田隆宏","en":"Takahiro Kishida","id":101403},{"ja":"松原秀典","en":"Hidenori Matsubara","id":107672},{"ja":"橋本敬史","en":"Takashi Hashimoto","id":118452},{"ja":"あべくにひろ","en":"Kunihiro Abe","pn":{"ja":"阿部邦博","en":"Kunihiro Abe"},"id":157762}]}
    },
]
// Randomize
// .map(value => ({ value, sort: Math.random() }))
// .sort((a, b) => a.sort - b.sort)
// .map(({ value }) => value);

// Methods
const formatNumber = (value) => {
    return UtilityService.formatNumber(value.toFixed(0));
};

// Head configuration
useHead({
    title: t('app.name'),
    meta: [
        {
            name: "og:site_name",
            content: t('app.name')
        },
        {
            name: "og:type",
            content: "website"
        },
        {
            name: "twitter:creator",
            content: "@KeyFrameStaff"
        },
        {
            name: "twitter:site",
            content: "@KeyFrameStaff"
        },
        {
            name: "og:title",
            content: "KeyFrame Staff List"
        },
        {
            name: "description",
            content: t('app.description')
        },
        {
            name: "og:description",
            content: t('app.description')
        },
    ]
});

// Load recent and airing staff lists on component mount
onMounted(async () => {
    recentStaffLists.value = [];
    airingStaffLists.value = [];

    const res = await fetch(`${import.meta.env.VITE_API_URL}/api/stafflists/preview.php`);
    if (res && res.ok) {
        const data = await res.json();
        
        recentStaffLists.value = data.recent;
        airingStaffLists.value = data.airing;

        totalStaffLists.value = data.total;
        totalUpdated.value = data.totalUpdated || 100;
        totalContributors.value = data.totalContributors || 93;
    }

    // Start carousel auto-slide (change every 5 seconds)
    carouselInterval = setInterval(() => {
        carouselIndex.value = (carouselIndex.value + 1) % exampleCarousel.length;
    }, 5000);
});

// Clean up carousel interval on component unmount
onUnmounted(() => {
    if (carouselInterval) {
        clearInterval(carouselInterval);
    }
});

// Handle dot click to change carousel
const goToSlide = (index) => {
    carouselIndex.value = index;
    // Reset the auto-slide timer
    if (carouselInterval) {
        clearInterval(carouselInterval);
    }
    carouselInterval = setInterval(() => {
        carouselIndex.value = (carouselIndex.value + 1) % exampleCarousel.length;
    }, 5000);
};
</script>

<template>
    <section class="relative overflow-hidden px-10 py-24 text-white bg-keyframe">
        <div class="relative z-10 flex flex-col xl:flex-row gap-20 mx-auto max-w-[70em]">
            <div class="flex-1">
                <h1 class="!text-6xl" translate="no">{{ t('app.name') }}</h1>

                <p class="mt-10 text-white/85">
                    {{ t('home.intro.catchphrase') }}
                </p>

                <p class="mt-6 text-white/85">
                    {{ t('home.intro.description') }}
                </p>

                <div class="flex justify-center xl:justify-normal gap-3 mt-10">
                    <router-link to="/staff-lists" class="flex items-center gap-3 bg-white text-keyframe-600 px-5 py-3 rounded-full font-semibold hover:translate-y-[-2px] hover:shadow-lg transition-transform duration-200">
                        <font-awesome-icon icon="fa-solid fa-eye" />
                        {{ t('home.intro.button') }}
                    </router-link>
                </div>
            </div>
            <div class="hidden xs:flex flex-col w-full max-w-[400px] mx-auto">
                <div class="carousel-viewport">
                    <transition name="slider">
                        <div :key="carouselIndex" class="carousel-slide border-[6px] border-keyframe-300 rounded-2xl overflow-hidden">
                            <RoleStaff :role="exampleCarousel[carouselIndex].roleData"></RoleStaff>
                        </div>
                    </transition>
                </div>

                <div class="flex flex-1 flex-col justify-end">            
                    <i18n-t keypath="home.intro.source" tag="p" class="mt-2 text-center text-sm text-white/70">
                        <template #animeName>
                            <a :href="`/staff/${exampleCarousel[carouselIndex].slug}#${exampleCarousel[carouselIndex].episode}`" target="_blank" class="text-white/85 font-semibold hover:underline">
                                {{ exampleCarousel[carouselIndex].name }} #{{ exampleCarousel[carouselIndex].episode }}
                            </a>
                        </template>
                    </i18n-t>
                    
                    <!-- Navigation dots -->
                    <div class="flex justify-center gap-2 mt-3">
                        <button
                            v-for="(_, index) in exampleCarousel"
                            :key="index"
                            @click="goToSlide(index)"
                            :class="[
                                'carousel-dot rounded-full transition-all duration-100',
                                carouselIndex === index
                                    ? 'bg-white w-2 h-2'
                                    : 'bg-white/50 w-2 h-2 hover:bg-white/75'
                            ]"
                            :aria-label="`Go to slide ${index + 1}`"
                            class="focus:outline-none"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Stats -->
    <section class="px-10 py-20 bg-app-surface0">
        <div class="mx-auto max-w-[70em]">
            <div class="grid gap-6 grid-cols-1 xl:grid-cols-3">
                <div class="flex flex-col border border-app-surface3 bg-app-surface0 rounded-2xl p-8 gap-2 shadow-sm">
                    <small class="text-slate-500 font-semibold tracking-wide text-sm capitalize">{{ t('home.stats.publishedStaffLists.title') }}</small>
                    <NumberAnimation
                        class="block text-4xl font-rubik text-slate-800 dark:text-slate-300"
                        :from="0"
                        :to="totalStaffLists"
                        :duration="1"
                        :format="formatNumber"
                        autoplay
                        easing="linear"
                    />
                    <p class="description text-slate-500 text-sm">
                        {{ t('home.stats.publishedStaffLists.description') }}
                    </p>
                </div>
                
                <div class="flex flex-col border border-app-surface3 bg-app-surface0 rounded-2xl p-8 gap-2 shadow-sm">
                    <small class="text-slate-500 font-semibold tracking-wide text-sm capitalize">{{ t('home.stats.updatedStaffLists.title') }}</small>
                    <NumberAnimation
                        class="block text-4xl font-rubik text-slate-800 dark:text-slate-300"
                        :from="0"
                        :to="totalUpdated"
                        :duration="1"
                        :format="formatNumber"
                        autoplay
                        easing="linear"
                    />
                    <p class="description text-slate-500 text-sm">
                        {{ t('home.stats.updatedStaffLists.description') }}
                    </p>
                </div>
                
                <div class="flex flex-col border border-app-surface3 bg-app-surface0 rounded-2xl p-8 gap-2 shadow-sm">
                    <small class="text-slate-500 font-semibold tracking-wide text-sm capitalize">{{ t('home.stats.contributors.title') }}</small>
                    <NumberAnimation
                        class="block text-4xl font-rubik text-slate-800 dark:text-slate-300"
                        :from="0"
                        :to="totalContributors"
                        :duration="1"
                        :format="formatNumber"
                        autoplay
                        easing="linear"
                    />
                    <p class="description text-slate-500 text-sm">
                        {{ t('home.stats.contributors.description') }}
                    </p>
                </div>
            </div>
        </div>
    </section>
    
    <!-- Airing Staff Lists -->
    <section class="px-10 py-20 bg-app-surface1">
        <div class="mx-auto max-w-[70em]">
            <h2>{{ t('home.stafflists.airing.title') }}</h2>

            <p class="mt-3 mb-10 text-slate-500">
                {{ t('home.stafflists.airing.description') }}
            </p>

            <ul class="grid gap-4 grid-cols-1 lg:grid-cols-2">
                <StaffListEmbed v-for="staffList of airingStaffLists" :staffList="staffList" />
            </ul>

            <div class="flex flex-row justify-end">
                <RouterLink :to="`/staff-lists?airing=RELEASING&year=${new Date().getFullYear()}&status=IN_PROGRESS`" class="mt-6 block btn btn-keyframe">
                    <font-awesome-icon icon="fa-solid fa-eye" />
                    {{ t('home.stafflists.airing.seeMore') }}
                </RouterLink>
            </div>
        </div>
    </section>

    <!-- Secondary Block with Recent Staff Lists -->
    <section class="px-10 py-20 bg-app-surface2">
        <div class="mx-auto max-w-[70em]">
            <h2>{{ t('home.stafflists.recent.title') }}</h2>

            <p class="mt-3 mb-10 text-slate-500">
                {{ t('home.stafflists.recent.description') }}
            </p>

            <ul class="grid gap-4 grid-cols-1 lg:grid-cols-2">
                <StaffListEmbed v-for="staffList of recentStaffLists" :staffList="staffList" />
            </ul>

            <div class="flex flex-row justify-end">
                <RouterLink to="/staff-lists" class="mt-6 block btn btn-keyframe">
                    <font-awesome-icon icon="fa-solid fa-eye" />
                    {{ t('common.seeMore') }}
                </RouterLink>
            </div>
        </div>
    </section>

    <Footer />
</template>

<style scoped>
.carousel-viewport {
    position: relative;
    overflow: hidden;
    border-radius: 0.75rem;
}

.carousel-slide {
    width: 100%;
}

.carousel-dot {
    appearance: none;
    border: 0;
    padding: 0;
    -webkit-tap-highlight-color: transparent;
}

.carousel-dot:focus,
.carousel-dot:focus-visible,
.carousel-dot:active {
    outline: none;
    box-shadow: none;
}

:deep(.role-staff.rounded-md) {
    border-radius: 0px !important;
}

/* Slider transition animation for carousel slides */
.slider-enter-active,
.slider-leave-active {
    transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s ease;
    will-change: transform, opacity;
}

.slider-leave-active {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
}

.slider-enter-from {
    transform: translateX(105%);
    opacity: 1;
}

.slider-enter-to {
    transform: translateX(0);
    opacity: 1;
}

.slider-leave-from {
    transform: translateX(0);
    opacity: 1;
}

.slider-leave-to {
    transform: translateX(-105%);
    opacity: 0;
}
</style>